import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Outreach() {
  const { t } = useTranslation();

  const stats = [
    { number: '150+', label: t('outreach_stats_visits') },
    { number: '45+', label: t('outreach_stats_families') },
    { number: '24/7', label: t('outreach_stats_available') }
  ];

  const processSteps = [
    {
      number: '01',
      title: t('outreach_step1_title'),
      description: t('outreach_step1_desc')
    },
    {
      number: '02', 
      title: t('outreach_step2_title'),
      description: t('outreach_step2_desc')
    },
    {
      number: '03',
      title: t('outreach_step3_title'),
      description: t('outreach_step3_desc')
    },
    {
      number: '04',
      title: t('outreach_step4_title'),
      description: t('outreach_step4_desc')
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Split Hero Section */}
      <section className="mt-16 grid grid-cols-1 lg:grid-cols-2 min-h-[70vh]">
        {/* Left - Image */}
        <div className="relative">
          <img
            src="https://readdy.ai/api/search-image?query=Two%20people%20sitting%20on%20stone%20steps%20in%20conversation%2C%20dusk%20lighting%2C%20Ukrainian%20setting%2C%20peaceful%20dialogue%2C%20supportive%20body%20language%2C%20natural%20warm%20light%2C%20emotional%20support%20moment%2C%20civilian%20clothes&width=800&height=800&seq=out001&orientation=squarish"
            alt="Two people in supportive conversation"
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Right - Content */}
        <div className="flex items-center justify-center p-8 lg:p-16 bg-stone-50">
          <div className="max-w-lg">
            <div className="text-xs uppercase tracking-wider text-amber-600 mb-4">What We Do</div>
            <h1 className="text-4xl lg:text-5xl font-medium text-stone-800 mb-6">{t('outreach_hero_title')}</h1>
            <p className="text-lg text-stone-600 leading-relaxed mb-8">
              {t('outreach_hero_subtitle')}
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl font-bold text-amber-600">{stat.number}</div>
                  <div className="text-xs text-stone-500 uppercase tracking-wide">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What Outreach Looks Like */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-medium text-stone-800 mb-6">{t('outreach_meaning_title')}</h2>
            <p className="text-lg text-stone-600 leading-relaxed">
              {t('outreach_meaning_desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="bg-stone-50 rounded-lg p-8">
              <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-heart-line text-xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-stone-800 mb-4">{t('outreach_home_title')}</h3>
              <p className="text-stone-600 leading-relaxed">
                {t('outreach_home_desc')}
              </p>
            </div>

            <div className="bg-stone-50 rounded-lg p-8">
              <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-phone-line text-xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-stone-800 mb-4">{t('outreach_phone_title')}</h3>
              <p className="text-stone-600 leading-relaxed">
                {t('outreach_phone_desc')}
              </p>
            </div>

            <div className="bg-stone-50 rounded-lg p-8">
              <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-message-3-line text-xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-stone-800 mb-4">{t('outreach_text_title')}</h3>
              <p className="text-stone-600 leading-relaxed">
                {t('outreach_text_desc')}
              </p>
            </div>

            <div className="bg-stone-50 rounded-lg p-8">
              <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-community-line text-xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-stone-800 mb-4">{t('outreach_group_title')}</h3>
              <p className="text-stone-600 leading-relaxed">
                {t('outreach_group_desc')}
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
            <p className="text-amber-800 italic text-center">
              "{t('outreach_quote')}"
            </p>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-medium text-stone-800 mb-6">{t('outreach_how_title')}</h2>
            <p className="text-lg text-stone-600 leading-relaxed">
              {t('outreach_how_desc')}
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 md:left-1/2 md:transform md:-translate-x-px top-0 bottom-0 w-0.5 bg-stone-300"></div>
            
            <div className="space-y-12">
              {processSteps.map((step, index) => (
                <div key={index} className="relative">
                  {/* Timeline marker */}
                  <div className="absolute left-6 md:left-1/2 md:transform md:-translate-x-1/2 w-4 h-4 bg-amber-500 rounded-full border-4 border-white shadow-sm"></div>
                  
                  {/* Content */}
                  <div className={`ml-16 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12' : 'md:ml-1/2 md:pl-12'}`}>
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                      <div className="text-3xl font-light text-amber-500/30 mb-2">{step.number}</div>
                      <h3 className="text-xl font-semibold text-stone-800 mb-3">{step.title}</h3>
                      <p className="text-stone-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-stone-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-medium mb-6">{t('outreach_cta_title')}</h2>
          <p className="text-lg text-stone-300 leading-relaxed mb-8 max-w-2xl mx-auto">
            {t('outreach_cta_desc')}
          </p>
          <a
            href="/contact"
            className="inline-block bg-white text-stone-800 px-8 py-4 rounded-full font-medium hover:bg-amber-500 hover:text-white transition-colors duration-300 whitespace-nowrap"
          >
            {t('outreach_cta_button')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}